import type {
  DestinationTranslationValue,
  ILocalizedDestinationMap,
} from '@/shared/api/types/destination-types'
import { normalizeLocalizedDataMap } from '@/shared/api/mappers/localized-data-mapper'

/** One editable row per language; a language the destination has no text for yet gets an empty row. */
export function mapLocalizedDataToTranslations(
  localizedData: ILocalizedDestinationMap | null | undefined,
  languages: string[],
): DestinationTranslationValue[] {
  const normalized = normalizeLocalizedDataMap(localizedData)
  return languages.map((language) => {
    const localized = normalized?.[language]
    return {
      language,
      title: localized?.title ?? '',
      name: localized?.name ?? '',
      description: localized?.description ?? '',
    }
  })
}

/**
 * Rows to validate/save: the form's rows, or empty rows for every language when the user never touched the
 * translations (so a destination with no texts still produces one row per language).
 */
export function resolveTranslationRows(
  translations: DestinationTranslationValue[],
  languages: string[],
): DestinationTranslationValue[] {
  return translations.length > 0 ? translations : mapLocalizedDataToTranslations(null, languages)
}

/** Trims every text so whitespace-only values count as empty and never reach the API. */
export function normalizeDestinationTranslations(
  translations: DestinationTranslationValue[],
): DestinationTranslationValue[] {
  return translations.map((item) => ({
    ...item,
    title: (item.title ?? '').trim(),
    name: (item.name ?? '').trim(),
    description: (item.description ?? '').trim(),
  }))
}

/** Converts the rows into the API's `localizedData` map (keyed by language). */
export function buildLocalizedDataPayload(
  translations: DestinationTranslationValue[],
): ILocalizedDestinationMap {
  return Object.fromEntries(
    normalizeDestinationTranslations(translations).map((item) => [
      item.language,
      { title: item.title, name: item.name, description: item.description },
    ]),
  )
}
